import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ApplicantShell from "../components/ApplicantShell";
import ApplicantCoachPanel from "../components/applicant/ApplicantCoachPanel";
import { useApplicantCoach } from "../components/applicant/ApplicantCoachContext";
import KvpReportTrigger from "../components/kvp/KvpReportTrigger";
import config from "../config/config";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { applicantCommunicationLanguageOptions } from "../config/applicantCommunicationLanguages";
import { buildAuthRequestConfig, clearStoredAuth, getStoredIdentity } from "../utils/authSession";

const sections = [
  {
    key: "person",
    title: "Persoenliche Daten",
    label_en: "personal details",
    reason_de: "Klare Basisdaten helfen, dein Profil eindeutig einzuordnen.",
    fields: ["firstname", "middlename", "lastname", "birthday", "birthplace", "birthcountry"],
  },
  {
    key: "contact",
    title: "Kontakt",
    label_en: "contact",
    reason_de: "Kontaktangaben sind wichtig, damit Rueckfragen und Benachrichtigungen dich erreichen.",
    fields: ["email", "mobile"],
  },
  {
    key: "address",
    title: "Adresse",
    label_en: "address",
    reason_de: "Ort und Adresse helfen bei regionaler Sichtbarkeit und passender Zuordnung.",
    fields: ["roadname", "housenumber", "aereacode", "town"],
  },
];

const fieldLabels = {
  firstname: { de: "Vorname", en: "first name" },
  middlename: { de: "Zweitname", en: "middle name" },
  lastname: { de: "Nachname", en: "last name" },
  birthday: { de: "Geburtstag", en: "birthday" },
  birthplace: { de: "Geburtsort", en: "place of birth" },
  birthcountry: { de: "Geburtsland", en: "country of birth" },
  email: { de: "E-Mail", en: "email" },
  mobile: { de: "Mobilnummer", en: "mobile number" },
  roadname: { de: "Strasse", en: "street" },
  housenumber: { de: "Hausnummer", en: "house number" },
  aereacode: { de: "PLZ", en: "postal code" },
  town: { de: "Ort", en: "town" },
};

function hasVisibleValue(value) {
  return String(value || "").trim().length > 0;
}

function formatLabelList(labels = []) {
  if (!labels.length) return "";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} und ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} und ${labels[labels.length - 1]}`;
}

export default function ApplicantEditProfile() {
  const navigate = useNavigate();
  const { registerCoachSurface } = useApplicantCoach();
  const userid = getStoredIdentity().userid || "";
  const [loadErr, setLoadErr] = useState("");
  const [preferredCommunicationLanguageSource, setPreferredCommunicationLanguageSource] = useState("fallback");
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");
  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting, isDirty } } = useForm({
    defaultValues: {
      userid, email: "", password: "", birthday: "", birthplace: "", birthcountry: "", mobile: "",
      firstname: "", middlename: "", lastname: "", roadname: "", housenumber: "", aereacode: "", town: "",
      preferred_communication_language: "",
    },
  });
  const isMainSystemLanguageSource = preferredCommunicationLanguageSource === "profile"
    || preferredCommunicationLanguageSource === "account"
    || preferredCommunicationLanguageSource === "applicant";
  const formValues = watch();
  const fullname = useMemo(
    () => [formValues.firstname, formValues.middlename, formValues.lastname].filter(Boolean).join(" ") || "Profil",
    [formValues.firstname, formValues.lastname, formValues.middlename]
  );
  const kvpBaseContext = useMemo(() => ({
    module_key: "profile",
    module_label: "Profil",
    last_error_code: loadErr ? "profile_workspace_error" : null,
  }), [loadErr]);
  const coachSurfaceContext = useMemo(() => {
    const sectionSignals = sections.map((section) => {
      const missingFields = section.fields.filter((field) => !hasVisibleValue(formValues[field]));
      const missingLabels = missingFields.map((field) => fieldLabels[field]?.de || field);
      return {
        key: section.key,
        title: section.title,
        label_en: section.label_en,
        reason_de: section.reason_de,
        filledCount: section.fields.length - missingFields.length,
        totalCount: section.fields.length,
        missingFields,
        missingLabels,
      };
    });
    const preferredLanguageLabel = applicantCommunicationLanguageOptions.find(
      (option) => option.value === (formValues.preferred_communication_language || "")
    )?.label || applicantCommunicationLanguageOptions[0]?.label || "Automatisch";
    const missingItems = sectionSignals
      .filter((section) => section.missingFields.length > 0)
      .map((section) => ({
        key: `profile_${section.key}`,
        label_de: section.title,
        label_en: section.label_en,
        reason_de: `${section.reason_de || "Hier fehlen noch sichtbare Angaben."} Offen sind ${formatLabelList(section.missingLabels)}.`,
        href: "/dashboard/applicant/edit-profile",
      }));

    if (!hasVisibleValue(formValues.preferred_communication_language)) {
      missingItems.push({
        key: "profile_communication_language",
        label_de: "Kommunikationssprache",
        label_en: "communication language",
        reason_de: isMainSystemLanguageSource
          ? "Die sichtbare Coach-Sprache ist noch nicht eindeutig gewaehlt."
          : preferredCommunicationLanguageSource === "funnel"
            ? "Die sichtbare Coach-Sprache kommt noch aus dem Funnel. Mit einer Speicherung hier wird Profil oder Konto zur Hauptquelle."
            : "Ohne gespeicherte Auswahl bleibt nur der kontrollierte Fallback aktiv.",
        href: "/dashboard/account",
      });
    }

    const completionPercent = Math.round(
      (sectionSignals.reduce((sum, section) => sum + section.filledCount, 0) / Math.max(1, sectionSignals.reduce((sum, section) => sum + section.totalCount, 0))) * 100
    );
    const coachNextStep = missingItems[0] || {
      key: "profile_application_followup",
      label_de: "Bewerbung weiter pflegen",
      label_en: "continue your application",
      reason_de: "Dein sichtbares Profil ist gut gefuellt. Als Naechstes helfen Bewerbung und Skills am meisten weiter.",
      href: "/dashboard/applicant/bewerbung",
    };

    return {
      screen_key: "applicant_profile",
      screen_label: "Profil",
      highlighted_step: `${coachNextStep.label_de}: ${coachNextStep.reason_de}`,
      surface_state_key: `profile:${completionPercent}:${missingItems.map((item) => item.key).join("-") || "complete"}:${formValues.preferred_communication_language || "auto"}:${loadErr ? "error" : "ok"}`,
      surface_summary: {
        surface_type: "profile",
        status_label: missingItems.length ? "Profil noch offen" : "Profil gut gepflegt",
        status_note: missingItems.length
          ? missingItems[0].reason_de
          : "Dein sichtbares Profil ist aktuell vollstaendig genug, um als stabile Basis zu dienen.",
        visible_profile_completion_percent: completionPercent,
        visible_display_name: fullname,
        visible_language_label: preferredLanguageLabel,
        visible_language_source: preferredCommunicationLanguageSource,
        visible_sections: sectionSignals.map((section) => ({
          key: section.key,
          label_de: section.title,
          filled_count: section.filledCount,
          total_count: section.totalCount,
          missing_labels: section.missingLabels,
        })),
        coach_next_step: coachNextStep,
        coach_missing_items: missingItems.slice(0, 4),
      },
    };
  }, [formValues, fullname, isMainSystemLanguageSource, loadErr, preferredCommunicationLanguageSource]);

  useEffect(() => {
    registerCoachSurface(coachSurfaceContext);
  }, [coachSurfaceContext, registerCoachSurface]);

  const handleAuthError = (error) => {
    if (error?.response?.status !== 401) return false;
    clearStoredAuth();
    const detail = String(error?.response?.data?.detail || error?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");
    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivitaet automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gueltig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  };

  useEffect(() => {
    if (!userid) {
      navigate("/login");
      return;
    }
    document.title = "Experts4Europe | Profil";
    axios.get(`${config.API_BASE_URL}/api/applicant-data?userid=${userid}`, buildAuthRequestConfig()).then((response) => {
      const data = response.data || {};
      setPreferredCommunicationLanguageSource(data.preferred_communication_language_source || "fallback");
      reset({
        userid: data.userid || userid,
        email: data.email || data["e-mail"] || "",
        birthday: data.birthday || "",
        birthplace: data.birthplace || "",
        birthcountry: data.birthcountry || "",
        mobile: data.mobile || "",
        firstname: data.firstname || "",
        middlename: data.middlename || "",
        lastname: data.lastname || "",
        roadname: data.roadname || "",
        housenumber: data.housenumber || "",
        aereacode: data.aereacode || "",
        town: data.town || "",
        preferred_communication_language: data.preferred_communication_language || "",
      });
    }).catch((error) => {
      if (handleAuthError(error)) return;
      setLoadErr("Fehler beim Laden deines Profils.");
    });
  }, [userid, reset, navigate]);

  const onSubmit = async (data) => {
    const payload = {
      userid,
      email: data.email,
      ...(data.password ? { password: data.password } : {}),
      birthday: data.birthday || null,
      birthplace: data.birthplace || null,
      birthcountry: data.birthcountry || null,
      mobile: data.mobile || null,
      firstname: data.firstname || null,
      middlename: data.middlename || null,
      lastname: data.lastname || null,
      roadname: data.roadname || null,
      housenumber: data.housenumber || null,
      aereacode: data.aereacode || null,
      town: data.town || null,
      preferred_communication_language: data.preferred_communication_language || null,
    };
    try {
      await axios.put(`${config.API_BASE_URL}/api/users`, payload, buildAuthRequestConfig({ headers: { "Content-Type": "application/json" } }));
      navigate("/dashboard/applicant");
    } catch (error) {
      if (handleAuthError(error)) return;
      setLoadErr("Fehler beim Speichern deines Profils.");
    }
  };

  return (
    <ApplicantShell
      title="Profil"
      subtitle="Hier pflegst du deine persoenlichen Daten, deinen Kontakt und deine Adresse in derselben Applicant-Struktur."
      actions={<Button className="applicant-cta-button" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>{isSubmitting ? "Speichert..." : "Profil speichern"}</Button>}
      notifications={[]}
      searchItems={[{ href: "/dashboard/applicant", label: "Dashboard", description: "Zur Mission Control" }, { href: "/dashboard/applicant/bewerbung", label: "Bewerbung", description: "CV und Skills weiter pflegen" }]}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="applicant-workspace-page applicant-workspace-stack">
        {loadErr ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{loadErr}</div> : null}

        <section className="applicant-workspace-hero applicant-workspace-hero--profile">
          <div className="applicant-workspace-hero__main">
            <span className="applicant-section-kicker">Profil</span>
            <h2>Deine Basis fuer Sichtbarkeit und Kontakt</h2>
            <p>Je klarer dein Profil ist, desto leichter koennen Firmen und das System dich richtig einordnen.</p>
          </div>
          <aside className="applicant-readiness-panel">
            <div className="applicant-readiness-panel__meta applicant-readiness-panel__meta--single">
              <div><span>Anzeigename</span><strong>{fullname}</strong></div>
              <div><span>UserID</span><strong>{userid || "Noch offen"}</strong></div>
            </div>
          </aside>
        </section>

        <section className="applicant-workspace-card">
          <div className="applicant-workspace-card__head">
            <div className="applicant-workspace-heading-block">
              <span className="applicant-section-kicker">Login</span>
              <div className="applicant-workspace-title-row">
                <h3>Zugang und E-Mail</h3>
                <KvpReportTrigger context={{ ...kvpBaseContext, section_key: "login", section_label: "Zugang und E-Mail" }} />
              </div>
            </div>
          </div>
          <div className="applicant-workspace-grid applicant-workspace-grid--double">
            <div className="applicant-workspace-field">
              <Label className="applicant-workspace-label">UserID</Label>
              <Input disabled {...register("userid")} className="applicant-field" />
            </div>
            <div className="applicant-workspace-field">
              <Label className="applicant-workspace-label">E-Mail</Label>
              <Input {...register("email", { required: true })} type="email" className="applicant-field" placeholder="name@example.com" />
              {errors.email ? <small>E-Mail ist erforderlich.</small> : null}
            </div>
          </div>
          <div className="applicant-workspace-field">
            <Label className="applicant-workspace-label">Passwort</Label>
            <Input {...register("password")} type="password" className="applicant-field" placeholder="Nur ausfuellen, wenn du es aendern willst" />
          </div>
        </section>

        <section className="applicant-workspace-card">
          <div className="applicant-workspace-card__head">
            <div className="applicant-workspace-heading-block">
              <span className="applicant-section-kicker">Kommunikation</span>
              <div className="applicant-workspace-title-row">
                <h3>Bevorzugte Coach-Sprache</h3>
                <KvpReportTrigger context={{ ...kvpBaseContext, section_key: "communication", section_label: "Bevorzugte Coach-Sprache" }} />
              </div>
            </div>
          </div>
          <div className="applicant-workspace-field">
            <Label className="applicant-workspace-label">Kommunikationssprache</Label>
            <select {...register("preferred_communication_language")} className="applicant-field">
              {applicantCommunicationLanguageOptions.map((option) => (
                <option key={option.value || "auto"} value={option.value}>{option.label}</option>
              ))}
            </select>
            <small>
              {isMainSystemLanguageSource
                ? "Diese Sprache ist bereits im Applicant-Hauptsystem gespeichert."
                : preferredCommunicationLanguageSource === "funnel"
                  ? "Aktuell kommt die Sprache noch aus dem Funnel. Mit dem Speichern hier wird Profil/Konto zur Hauptquelle."
                  : "Wenn du hier nichts festlegst, bleibt nur der Fallback aktiv."}
            </small>
          </div>
        </section>

        {sections.map((section) => (
          <section key={section.key} className="applicant-workspace-card">
            <div className="applicant-workspace-card__head">
              <div className="applicant-workspace-heading-block">
                <span className="applicant-section-kicker">{section.title}</span>
                <div className="applicant-workspace-title-row">
                  <h3>{section.title}</h3>
                  <KvpReportTrigger context={{ ...kvpBaseContext, section_key: section.key, section_label: section.title }} />
                </div>
              </div>
            </div>
            <div className="applicant-workspace-grid applicant-workspace-grid--double">
              {section.fields.includes("firstname") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Vorname</Label><Input {...register("firstname", { required: true })} className="applicant-field" />{errors.firstname ? <small>Pflichtfeld.</small> : null}</div> : null}
              {section.fields.includes("middlename") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Zweitname</Label><Input {...register("middlename")} className="applicant-field" /></div> : null}
              {section.fields.includes("lastname") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Nachname</Label><Input {...register("lastname", { required: true })} className="applicant-field" />{errors.lastname ? <small>Pflichtfeld.</small> : null}</div> : null}
              {section.fields.includes("birthday") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Geburtstag</Label><Input type="date" {...register("birthday")} className="applicant-field" /></div> : null}
              {section.fields.includes("birthplace") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Geburtsort</Label><Input {...register("birthplace")} className="applicant-field" /></div> : null}
              {section.fields.includes("birthcountry") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Geburtsland</Label><Input {...register("birthcountry")} className="applicant-field" /></div> : null}
              {section.fields.includes("mobile") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Mobil</Label><Input {...register("mobile")} className="applicant-field" placeholder="+49 ..." /></div> : null}
              {section.fields.includes("roadname") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Strasse</Label><Input {...register("roadname")} className="applicant-field" /></div> : null}
              {section.fields.includes("housenumber") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Hausnummer</Label><Input {...register("housenumber")} className="applicant-field" /></div> : null}
              {section.fields.includes("aereacode") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">PLZ</Label><Input {...register("aereacode")} className="applicant-field" /></div> : null}
              {section.fields.includes("town") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Ort</Label><Input {...register("town")} className="applicant-field" /></div> : null}
            </div>
          </section>
        ))}

        {isDirty ? (
          <div className="applicant-workspace-actions applicant-workspace-actions--sticky">
            <span className="applicant-status-pill">Nicht gespeichert</span>
            <Button className="applicant-cta-button" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>{isSubmitting ? "Speichert..." : "Jetzt speichern"}</Button>
          </div>
        ) : null}
      </form>

      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/")}>Startseite</Button>
            <Button className="applicant-cta-button" onClick={() => navigate("/login", { replace: true })}>Erneut anmelden</Button>
          </div>
        </DialogContent>
      </Dialog>
      <ApplicantCoachPanel />
    </ApplicantShell>
  );
}
